import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import NutriScore from './NutriScore';
import { TAGS } from '../mock';
import { SHOW_PRICES } from '../config';
import { mealKcal } from '../lib/nutrition';

export default function DishCard({ dish }) {
  const { tf, t, addToCart, lang, menuState } = useApp();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);

  const handleAdd = (e) => {
    e.stopPropagation();
    addToCart(dish.id, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  const tagList = (dish.tags || []).filter((x) => x === 'vegetarien' || x === 'vegan' || x === 'fit' || x === 'proteine' || x === 'sans-gluten' || x === 'poisson');

  return (
    <div
      onClick={() => navigate(`/product/${dish.id}`)}
      className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-neutral-200/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-square overflow-hidden bg-neutral-100">
        <img src={dish.image} alt={tf(dish.name)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {/* top-left tags */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5 items-start">
          {tagList.map((tag) => (
            <span key={tag} className="text-[10px] font-bold uppercase tracking-wide whitespace-nowrap text-white px-2 py-1 rounded-full shadow"
              style={{ background: tag === 'fit' ? '#c0873a' : tag === 'poisson' ? '#3b6e8f' : '#5a8a3a' }}>
              {(TAGS[tag] && (TAGS[tag])[lang]) || tag}
            </span>
          ))}
        </div>
        {/* nutri score */}
        {dish.nutriScore && !['entrees', 'desserts'].includes(dish.category) && (
          <div className="absolute top-2.5 right-2.5">
            <NutriScore score={dish.nutriScore} size={32} />
          </div>
        )}
        {/* hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 bg-white text-[#2f3826] text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
            {t('view_details')}
          </span>
        </div>
      </div>

      <div className="p-3.5">
        <h3 className="font-display text-[17px] leading-snug text-[#2f3826] line-clamp-2 min-h-[46px]">{tf(dish.name)}</h3>
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-[13px] text-neutral-500">
            {mealKcal(dish) ? `${mealKcal(dish)} ${t('kcal')} · ` : ''}{dish.weight ? `${dish.weight}g` : ''}
          </p>
        </div>
        <div className="mt-3 flex items-center justify-between">
          {SHOW_PRICES ? (
            <span className="font-semibold text-[#4b5e34]">CHF {dish.price?.toFixed(2)}</span>
          ) : <span />}
          {dish.availability === 'week2' && !menuState.open ? (
            <span data-testid={`preorder-closed-badge-${dish.id}`} className="text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
              {t('preorder_closed_short')}
            </span>
          ) : dish.availability === 'week2' ? (
            <button
              onClick={handleAdd}
              data-testid={`add-to-cart-${dish.id}`}
              className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${added ? 'bg-[#5a8a3a] text-white' : 'bg-[#f0ede4] text-[#4b5e34] hover:bg-[#e4ddcb]'}`}
            >
              {added ? <><Check size={15} /> {t('added')}</> : <><Plus size={15} /> {t('order')}</>}
            </button>
          ) : (
            <span data-testid={`in-fridge-badge-${dish.id}`} className="text-[11px] font-medium text-[#4b5e34] bg-[#eef0e6] px-2.5 py-1 rounded-full">
              {t('in_fridge_badge')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
